<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { selectProps, type SelectOption } from 'naive-ui';
import GaiaRemoteSelect, { type GaiaRemotePage } from '../../base/selects/GaiaRemoteSelect.vue';
import { PermissionsService } from '@/services/permissionsService';
import type { UserInDb } from '@/api';

/**
 * Picks one or more people from the organisation, by uid.
 *
 * A remote search rather than a list loaded once: the endpoint caps a page at
 * 50, so every caller that loaded "all users" up front was silently truncating
 * at 50 and offering no way to reach the rest.
 */
const props = defineProps({
  ...selectProps,
  /** Users per request. The search endpoint caps this at 50. */
  pageSize: {
    type: Number,
    default: 50,
  },
  /**
   * Users to keep pickable even when the search does not return them —
   * a deactivated assignee still on a record, say. Same idea as
   * GaiaRemoteSelect's `seedOptions`, taking users rather than options.
   */
  seedUsers: {
    type: Array as PropType<UserInDb[]>,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Search people',
  },
});

/**
 * Both parts when there are both: two colleagues can share a display name, and
 * the email is what tells them apart. Matches the label the calendar link
 * loader built before this component existed.
 */
function toOption(user: UserInDb): SelectOption {
  return {
    label: user.full_name ? `${user.full_name} (${user.email})` : user.email,
    value: user.uid,
  };
}

const seedOptions = computed<SelectOption[]>(() => props.seedUsers.map(toOption));

async function fetchUsers(query: string, page: number): Promise<GaiaRemotePage> {
  const { results, result_count } = await PermissionsService.searchUsers({
    text: query,
    page,
    pageSize: props.pageSize,
    sortKey: 'email',
    sortDirection: 'asc',
  });
  return {
    options: results.map(toOption),
    hasMore: page * props.pageSize < result_count,
  };
}

const selectBindings = computed<Record<string, unknown>>(() => {
  const bindings: Record<string, unknown> = { ...props };
  delete bindings.pageSize;
  delete bindings.seedUsers;
  return bindings;
});
</script>

<template>
  <gaia-remote-select v-bind="selectBindings" :fetch="fetchUsers" :seed-options="seedOptions" />
</template>
